// biome-ignore-all lint/security/noDangerouslySetInnerHtml: Revision HTML is server-sanitized before storage.
import { useEffect, useState } from 'react';

import type { RevisionListResponse, RevisionRead, RevisionSummary } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import type { DiffHunk, DiffLine } from '@/features/diff/UnifiedDiffView';
import { UnifiedDiffView } from '@/features/diff/UnifiedDiffView';
import { useT } from '@/i18n/LocaleProvider';

export type RevisionHistoryProps = {
  lang: string;
  slug: string;
  revisionID?: string;
  currentMarkdown?: string;
  onBack?: () => void;
  onSelectRevision?: (revisionID: string) => void;
};

const dateFormatter = new Intl.DateTimeFormat('ru', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function RevisionHistory({
  lang,
  slug,
  revisionID,
  currentMarkdown,
  onBack,
  onSelectRevision,
}: RevisionHistoryProps) {
  const t = useT();
  const [revisions, setRevisions] = useState<RevisionSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedRevision, setSelectedRevision] = useState<RevisionRead | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [revisionError, setRevisionError] = useState<string | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingRevision, setIsLoadingRevision] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingList(true);
    setListError(null);

    wikiApiClient
      .listRevisions(lang, slug)
      .then((result: RevisionListResponse) => {
        if (cancelled) return;
        setRevisions(result.revisions);
        setNextCursor(result.next_cursor ?? null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setListError(errorMessage(error, t('history_error_load')));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingList(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, slug, t]);

  useEffect(() => {
    if (!revisionID) {
      setSelectedRevision(null);
      setShowDiff(false);
      return;
    }

    let cancelled = false;
    setIsLoadingRevision(true);
    setRevisionError(null);
    setSelectedRevision(null);

    wikiApiClient
      .getRevision(revisionID)
      .then((rev: RevisionRead) => {
        if (!cancelled) setSelectedRevision(rev);
      })
      .catch((error: unknown) => {
        if (!cancelled) setRevisionError(errorMessage(error, t('history_error_revision')));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRevision(false);
      });

    return () => {
      cancelled = true;
    };
  }, [revisionID, t]);

  const diffHunks =
    showDiff && selectedRevision
      ? computeLineDiff(selectedRevision.markdown ?? '', currentMarkdown ?? '')
      : null;

  return (
    <article className="note" data-testid="revision-history">
      <header className="note-header">
        <div className="note-kicker">
          <button type="button" onClick={onBack}>
            {t('history_back')}
          </button>
        </div>
        <h1>{t('history_title', { slug })}</h1>
      </header>

      {listError ? <div className="empty-note">{listError}</div> : null}
      {isLoadingList && revisions.length === 0 ? (
        <div className="empty-note">{t('history_loading_list')}</div>
      ) : null}
      {!isLoadingList && !listError && revisions.length === 0 ? (
        <div className="empty-note">{t('history_no_revisions')}</div>
      ) : null}

      {revisions.length > 0 ? (
        <ul className="revision-list">
          {revisions.map((rev) => (
            <li key={rev.id} className={`revision-row${rev.id === revisionID ? ' is-active' : ''}`}>
              <button
                type="button"
                onClick={() => {
                  setShowDiff(false);
                  onSelectRevision?.(rev.id);
                }}
              >
                <time dateTime={rev.published_at}>{formatRevDate(rev.published_at)}</time>
                <span title={rev.author?.email ?? undefined}>
                  {rev.author?.name ?? rev.published_by}
                </span>
                {rev.summary ? <span className="revision-summary">{rev.summary}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {nextCursor ? <div className="empty-note">{t('history_not_all')}</div> : null}

      {revisionID ? (
        <section className="note-body revision-detail" data-testid="revision-detail">
          {isLoadingRevision ? (
            <div className="empty-note">{t('history_loading_revision')}</div>
          ) : null}
          {revisionError ? <div className="empty-note">{revisionError}</div> : null}

          {selectedRevision ? (
            <>
              <div className="old-version-banner" role="alert" data-testid="old-version-banner">
                <span>{t('history_old_version')}</span>
                <button type="button" onClick={() => setShowDiff((prev) => !prev)}>
                  {showDiff ? t('history_hide_compare') : t('history_compare')}
                </button>
              </div>

              {showDiff && diffHunks ? <UnifiedDiffView hunks={diffHunks} /> : null}

              {!showDiff && selectedRevision.rendered_html ? (
                <div
                  className="note-body"
                  dangerouslySetInnerHTML={{ __html: selectedRevision.rendered_html }}
                />
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
    </article>
  );
}

function formatRevDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateFormatter.format(date);
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function computeLineDiff(oldText: string, newText: string): DiffHunk[] {
  const a = oldText ? oldText.split('\n') : [];
  const b = newText ? newText.split('\n') : [];
  const m = a.length;
  const n = b.length;

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  type RawEdit = { type: 'equal' | 'delete' | 'insert'; content: string };
  const script: RawEdit[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      script.unshift({ type: 'equal', content: a[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      script.unshift({ type: 'insert', content: b[j - 1] });
      j--;
    } else {
      script.unshift({ type: 'delete', content: a[i - 1] });
      i--;
    }
  }

  const lines: DiffLine[] = [];
  let oldNum = 1;
  let newNum = 1;

  for (const edit of script) {
    if (edit.type === 'equal') {
      lines.push({ type: 'equal', old_line: oldNum++, new_line: newNum++, content: edit.content });
    } else if (edit.type === 'delete') {
      lines.push({ type: 'delete', old_line: oldNum++, content: edit.content });
    } else {
      lines.push({ type: 'insert', new_line: newNum++, content: edit.content });
    }
  }

  if (lines.every((l) => l.type === 'equal')) return [];

  const CONTEXT = 3;
  const changedIndices = lines.reduce<number[]>((acc, l, idx) => {
    if (l.type !== 'equal') acc.push(idx);
    return acc;
  }, []);

  const mergedRanges: [number, number][] = [];
  for (const ci of changedIndices) {
    const ctxStart = Math.max(0, ci - CONTEXT);
    const ctxEnd = Math.min(lines.length - 1, ci + CONTEXT);
    const last = mergedRanges[mergedRanges.length - 1];
    if (last && ctxStart <= last[1] + 1) {
      last[1] = Math.max(last[1], ctxEnd);
    } else {
      mergedRanges.push([ctxStart, ctxEnd]);
    }
  }

  return mergedRanges.map(([start, end]) => {
    const hunkLines = lines.slice(start, end + 1);
    const firstOld = hunkLines.find((l) => l.old_line !== undefined)?.old_line ?? 1;
    const firstNew = hunkLines.find((l) => l.new_line !== undefined)?.new_line ?? 1;
    return {
      old_start: firstOld,
      old_lines: hunkLines.filter((l) => l.type !== 'insert').length,
      new_start: firstNew,
      new_lines: hunkLines.filter((l) => l.type !== 'delete').length,
      lines: hunkLines,
    };
  });
}
